import { useState, useRef, useEffect } from "react";
import {
  Box, Typography, Button, TextField, Paper,
  InputAdornment, IconButton, Divider, Grid, Card
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
 
// ─── Helpers ──────────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS   = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
 
const today   = () => new Date();
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const isSame  = (a, b) => a && b && a.toDateString() === b.toDateString();
const fmt     = (d) => d ? `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}` : "";
 
const PRESETS = [
  { label: "Today",        range: () => [today(), today()] },
  { label: "Yesterday",    range: () => [addDays(today(),-1), addDays(today(),-1)] },
  { label: "Last 7 days",  range: () => [addDays(today(),-6), today()] },
  { label: "Last 30 days", range: () => [addDays(today(),-29), today()] },
  { label: "Last month",   range: () => { const d=today(); return [new Date(d.getFullYear(),d.getMonth()-1,1), new Date(d.getFullYear(),d.getMonth(),0)]; }},
  { label: "This month",   range: () => [new Date(today().getFullYear(), today().getMonth(), 1), today()] },
  { label: "This year",    range: () => [new Date(today().getFullYear(), 0, 1), today()] },
];
 
// ─── Styled components ────────────────────────────────────────────────────────
const PresetItem = styled(Box)(({ selected }) => ({
  padding: "9px 16px",
  fontSize: 14,
  cursor: "pointer",
  color: selected ? "#0d9488" : "#374151",
  fontWeight: selected ? 600 : 400,
  background: selected ? "#f0fdfa" : "transparent",
  "&:hover": { background: "#f8fafc" },
  transition: "background 0.12s",
  whiteSpace: "nowrap",
}));
 
const DayCell = styled(Box)(({ istoday, isselected, isrange, isother }) => ({
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  fontSize: 14,
  cursor: isother ? "default" : "pointer",
  fontWeight: istoday || isselected ? 600 : 400,
  color: isselected
    ? "#fff"
    : istoday
    ? "#0d9488"
    : isother
    ? "#d1d5db"
    : "#374151",
  background: isselected
    ? "#0d9488"
    : isrange
    ? "#ccfbf1"
    : "transparent",
  border: istoday && !isselected ? "1.5px solid #0d9488" : "none",
  transition: "background 0.15s",
  "&:hover": {
    background: isother
      ? "transparent"
      : isselected
      ? "#0d9488"
      : "#f0fdfa",
  },
}));
 
const DateInput = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    fontSize: 13,
    borderRadius: 8,
    height: 36,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#0d9488", borderWidth: 1.5 },
  },
  "& .MuiInputBase-input": {
    padding: "0 8px",
    fontSize: 13,
    color: "#374151",
    "&::placeholder": { color: "#9ca3af" },
  },
});
 
const TriggerButton = styled(Button)({
  textTransform: "none",
  fontSize: 13,
  color: "#374151",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "6px 12px",
  background: "#fff",
  gap: 8,
  "&:hover": { background: "#f8fafc", border: "1px solid #cbd5e1" },
});
 
// ─── Mini Calendar ────────────────────────────────────────────────────────────
function MiniCalendar({ value, onChange, rangeStart, rangeEnd }) {
  const [view, setView] = useState(new Date(value || today()));
  const year = view.getFullYear(), month = view.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
 
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push({ d: new Date(year, month-1, prevDays - firstDow + i + 1), other: true });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ d: new Date(year, month, d), other: false });
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length-1].d;
    cells.push({ d: new Date(last.getFullYear(), last.getMonth(), last.getDate()+1), other: true });
  }
 
  const inRange = (d) => rangeStart && rangeEnd && d > rangeStart && d < rangeEnd;
 
  return (
    <Box sx={{ width: 280 }}>
      {/* Month nav */}
      <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:1.5 }}>
        <IconButton size="small" onClick={() => setView(new Date(year, month-1, 1))}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize:14, fontWeight:600, color:"#1e293b" }}>
          {MONTHS[month]} {year}
        </Typography>
        <Box sx={{ display:"flex", alignItems:"center", gap:0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setView(today())}
            sx={{
              textTransform:"none", fontSize:12, py:0.3, px:1.2, minWidth:0,
              borderRadius:1.5, border:"1px solid #e2e8f0", color:"#374151",
              "&:hover": { background:"#f8fafc", border:"1px solid #cbd5e1" }
            }}
          >Today</Button>
          <IconButton size="small" onClick={() => setView(new Date(year, month+1, 1))}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
 
      {/* Day headers */}
      <Box sx={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", mb:0.5, textAlign:"center" }}>
        {DAYS.map(d => (
          <Typography key={d} sx={{ fontSize:11, fontWeight:500, color:"#9ca3af", py:0.5 }}>{d}</Typography>
        ))}
      </Box>
 
      {/* Day cells */}
      <Box sx={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:0.3, justifyItems:"center" }}>
        {cells.map(({ d, other }, i) => (
          <DayCell
            key={i}
            istoday={isSame(d, today()) && !other ? 1 : 0}
            isselected={isSame(d, value) && !other ? 1 : 0}
            isrange={inRange(d) && !other ? 1 : 0}
            isother={other ? 1 : 0}
            onClick={() => !other && onChange(d)}
          >
            {d.getDate()}
          </DayCell>
        ))}
      </Box>
    </Box>
  );
}
 
// ─── Date Range Picker ────────────────────────────────────────────────────────
export function DateRangePicker({ onChange }) {
  const [open, setOpen]         = useState(false);
  const [startDate, setStart]   = useState(null);
  const [endDate, setEnd]       = useState(null);
  const [picking, setPicking]   = useState("start");
  const [activePreset, setPreset] = useState(null);
  const ref = useRef();
  const openRef = useRef(open);
  const startRef = useRef(startDate);
  const endRef = useRef(endDate);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    openRef.current = open;
  }, [open]);
  useEffect(() => {
    startRef.current = startDate;
    endRef.current = endDate;
  }, [startDate, endDate]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /** If user picked only start then closed the calendar, treat end as today and notify parent. */
  const finalizeIncompleteRange = () => {
    const s = startRef.current;
    const en = endRef.current;
    if (!s || en) return;
    const t = today();
    endRef.current = t;
    setEnd(t);
    setPicking("start");
    onChangeRef.current?.(s, t);
  };

  const requestClose = () => {
    finalizeIncompleteRange();
    setOpen(false);
  };

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target) && openRef.current) {
        requestClose();
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
 
  const applyPreset = (preset) => {
    const [s, e] = preset.range();
    setStart(s); setEnd(e); setPreset(preset.label);
    onChange?.(s, e); setOpen(false);
  };
 
  const handleDay = (d) => {
    setPreset(null);
    if (picking === "start") { setStart(d); setEnd(null); setPicking("end"); }
    else {
      const [s, e] = d < startDate ? [d, startDate] : [startDate, d];
      setEnd(e); setStart(s); setPicking("start");
      onChange?.(s, e); setOpen(false);
    }
  };
 
  const label = startDate && endDate
    ? `${fmt(startDate)} - ${fmt(endDate)}`
    : "dd/mm/yyyy - dd/mm/yyyy";
 
  return (
    <Box ref={ref} sx={{ position:"relative", display:"inline-block" }}>
      <TriggerButton
        onClick={() => (open ? requestClose() : setOpen(true))}
        endIcon={<CalendarTodayOutlinedIcon sx={{ fontSize:"14px !important", color:"#9ca3af" }} />}
        sx={{ color: startDate ? "#374151" : "#9ca3af", minWidth: 210,display:"flex", justifyContent:"space-between",alignItems:"center" }}
      >
        {label}
      </TriggerButton>
 
      {open && (
        <Paper elevation={3} sx={{
          position:"absolute", right:0, top:"calc(100% + 6px)", zIndex:1300,
          borderRadius:2.5, border:"1px solid #e2e8f0", overflow:"hidden",
          display:"flex", boxShadow:"0 8px 30px rgba(0,0,0,0.12)"
        }}>
          {/* Presets */}
          <Box sx={{ borderRight:"1px solid #f1f5f9", py:1, minWidth:140 }}>
            {PRESETS.map(p => (
              <PresetItem key={p.label} selected={activePreset === p.label ? 1 : 0} onClick={() => applyPreset(p)}>
                {p.label}
              </PresetItem>
            ))}
          </Box>
 
          {/* Calendar side */}
          <Box sx={{ p:2 }}>
            {/* Two date inputs */}
            <Box sx={{ display:"flex", gap:1, mb:2 }}>
              {[["start", startDate], ["end", endDate]].map(([key, val]) => (
                <DateInput
                  key={key}
                  value={val ? fmt(val) : ""}
                  placeholder="dd/mm/yyyy"
                  onClick={() => setPicking(key)}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarTodayOutlinedIcon sx={{ fontSize:14, color: picking===key ? "#0d9488" : "#9ca3af" }} />
                      </InputAdornment>
                    ),
                    sx: { borderColor: picking===key ? "#0d9488" : undefined }
                  }}
                  sx={{ width:140, "& .MuiOutlinedInput-root fieldset": picking===key ? { borderColor:"#0d9488 !important", borderWidth:"1.5px !important" } : {} }}
                />
              ))}
            </Box>
 
            <MiniCalendar
              value={picking === "start" ? startDate : endDate}
              rangeStart={startDate}
              rangeEnd={endDate}
              onChange={handleDay}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
}